// src/pages/Events/EventBookings.tsx
import { Card } from "react-bootstrap";
import "./Events.css";
import CustomTable from "../../components/CustomTable/CustomTable";
import WebService from "../../utility/WebService";
import HelperService from "../../utility/HelperService";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EyeFill } from "react-bootstrap-icons";
import Transaction from "../Customer/Transaction";

const EventBookings = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState<any>(null);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  // master list: ticket-wise rows
  const [allBookings, setAllBookings] = useState<any[]>([]);

  // filtered list for current tab
  const [bookings, setBookings] = useState<any[]>([]);

  const [uniqueTickets, setUniqueTickets] = useState<string[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<string>("");

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // initial data load / pagination
  useEffect(() => {
    if (eventId) {
      getEventDetails();
      getEventBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, currentPage]);

  // tab change par sirf filter
  useEffect(() => {
    let filtered = [...allBookings];

    // 🎫 Ticket filter
    if (selectedTicket) {
      filtered = filtered.filter((row: any) => row.ticketNames?.includes(selectedTicket));
    }

    // 💳 Payment Status filter
    if (selectedStatus) {
      filtered = filtered.filter((row: any) => row.status?.toLowerCase() === selectedStatus.toLowerCase());
    }

    if (selectedPaymentMethod) {
      filtered = filtered.filter(
        (row: any) =>
          row.type?.toLowerCase() === selectedPaymentMethod.toLowerCase()
      );
    }

    // 📅 Booking Date filter
    if (fromDate) {
      filtered = filtered.filter((row: any) => new Date(row.bookingDate) >= new Date(fromDate));
    }

    if (toDate) {
      filtered = filtered.filter((row: any) => new Date(row.bookingDate) <= new Date(toDate));
    }

    // 🔍 Search filter
    if (searchText.trim()) {
      const q = searchText.toLowerCase();

      filtered = filtered.filter(
        (row: any) =>
          row.bookingId?.toLowerCase().includes(q) ||
          row.billingName?.toLowerCase().includes(q) ||
          row.billingEmail?.toLowerCase().includes(q) ||
          row.transactionId?.toLowerCase().includes(q),
      );
    }
    setBookings(filtered);
    setTotalCount(filtered.length);
    // setCurrentPage(1);
  }, [selectedTicket, selectedStatus, selectedPaymentMethod, allBookings, searchText, fromDate, toDate]);

  const getEventDetails = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `events/event-booking-detail/${eventId}`,
      });
      const eventData = res?.data?.data || res?.data || {};
      setEvent(eventData);
    } catch (err) {
      console.error("Failed to load event details", err);
    }
  };

  const toNumber = (val: any) => {
    if (!val) return 0;

    if (typeof val === "number") return val;

    if (typeof val === "string") return Number(val);

    if (val?.$numberDecimal) return Number(val.$numberDecimal);

    return 0;
  };

  const getEventBookings = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `events/event-booking-list/${eventId}`,
      });

      const perm = res?.permissions || {};
      setPagePermission({
        canView: perm.canView,
        canAdd: perm.canAdd,
        canEdit: perm.canEdit,
        canDelete: perm.canDelete,
      });
      const payload = res?.data?.data ?? res?.data ?? [];
      const pagination = res?.data?.pagination ?? res?.pagination ?? {};

      const bookingsList: any[] = Array.isArray(payload) ? payload : [];

      // 🔥 1 booking → N ticket-rows
      const bookingRows: any[] = bookingsList.map((booking: any) => {
        const billingName = booking?.billing?.address?.name || "-";
        const billingEmail = booking?.billing?.address?.email || "-";
        const billingPhone = booking?.billing?.address?.phone || "-";
        const billingAddress = booking?.billing?.address?.addressLine || "-";
        const country = booking?.billing?.address?.country?.name || "-";
        const state = booking?.billing?.address?.state?.name || "-";
        const city = booking?.billing?.address?.city?.name || "-";
        const zip = booking?.billing?.address?.zip || "-";
        const companyName = booking?.billing?.companyName || "";
        const gstNumber = booking?.billing?.gstNumber || "";
        const status = booking?.payment?.status || "PENDING";
        const ticketNamesInBooking = (booking.tickets || []).map((t: any) => t.name);
        const subTotal = toNumber(booking?.subTotal);
        const taxAmount = toNumber(booking?.taxAmount);
        const discount = toNumber(booking?.discountedAmount);
        const couponCode = booking?.coupon?.code || ""

        // 🔥 TOTAL attendees (all tickets)
        const attendeesCount = (booking.tickets || []).reduce(
          (sum: number, t: any) => sum + ((t.attendees && t.attendees.length) || t.count || 0),
          0,
        );

        const transactionDateAndTime =
          status !== "PENDING" && booking?.transactionId?.createdAt
            ? new Date(booking.transactionId.createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            : "-";

        const attendeesData: any[] = [];

        (booking.tickets || []).forEach((ticket: any) => {
          (ticket.attendees || []).forEach((att: any) => {
            attendeesData.push({
              name: att.name,
              email: att.email || "",
              phone: att.phone || "",
              ticketName: ticket.name || "",
              ticketNumber: att.ticketNumber || "",
            });
          });
        });

        return {
          _id: booking._id,
          bookingId: booking.orderNo,
          type: booking.type,
          transactionId: booking.payment?.gatewayId || "-",

          bookingDate: booking.createdAt,
          bookingDateFormatted: HelperService.dateFormatter(booking.createdAt),

          paidAmount: `₹${toNumber(booking.totalAmount)} INR`,
          paid: toNumber(booking.totalAmount),
          transactionDateAndTime,

          billingName,
          billingEmail,
          billingPhone,
          billingAddress,
          country,
          state,
          city,
          zip,
          companyName,
          gstNumber,
          subTotal,
          taxAmount,
          discount,
          couponCode,

          attendeesCount,
          attendeesData,

          ticketNames: ticketNamesInBooking,

          status,
          statusTag: (
            <span
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: status === "PAID" ? "#dff5e9" : status === "PENDING" ? "#fff7df" : "#fde7ea",
                color: status === "PAID" ? "#1b7a2d" : status === "PENDING" ? "#8a6d00" : "#8a1d2b",
                fontSize: 12,
                fontWeight: 700,
              }}>
              {status}
            </span>
          ),

          viewAction: (
            <span
              className="cursor-pointer"
              onClick={() => navigate(`/event-bookings/details/${booking._id}`)}
              style={{
                fontSize: 18,
                color: "#0d6efd",
                display: "inline-flex",
                alignItems: "center",
              }}>
              <EyeFill />
            </span>
          ),
        };
      });

      // unique ticket names for tabs
      // const unique = Array.from(new Set(ticketRows.map((row) => row.ticket)));
      // setUniqueTickets(unique);

      const ticketNameSet = new Set<string>();

      bookingsList.forEach((booking: any) => {
        (booking.tickets || []).forEach((t: any) => {
          if (t.name) ticketNameSet.add(t.name);
        });
      });

      const ticketNames = Array.from(ticketNameSet);

      if (ticketNames.length > 1) {
        setUniqueTickets(ticketNames);
      } else {
        setUniqueTickets([]); // ❌ no tabs
        setSelectedTicket(""); // safety reset
      }

      // master + initial view
      setAllBookings(bookingRows);
      setBookings(bookingRows);
      setTotalCount(bookingRows.length); // total visible rows (All tab)

      // (optional) if you want server count instead:
      // setTotalCount(pagination?.total ?? ticketRows.length);
    } catch (err) {
      console.error("Failed to load bookings", err);
      setAllBookings([]);
      setBookings([]);
      setTotalCount(0);
    }
  };

  // ------------- EXPORT CSV -----------------
  const handleExport = () => {
    console.log(bookings, "???")
    if (!bookings.length) return;

    // 1️⃣ Find max attendees count
    let maxAttendees = 0;

    bookings.forEach((row: any) => {
      const count = row.attendeesData?.length || 0;
      if (count > maxAttendees) maxAttendees = count;
    });
    // 2️⃣ Prepare CSV rows
    const data = bookings.map((row: any) => {
      const baseRow: any = {
        "Event Name": event?.title || "",
        "Event Date": `${HelperService.dateFormatter(event?.start_date)} - ${HelperService.dateFormatter(event?.end_date)}`,
        "Event Location": event?.location || "",
        "Ticket Type": event?.ticketType || "",
        "Ticket Price": row.subTotal || 0,
        bookingId: row.bookingId,
        "Order ID": row.transactionId,
        "Transaction Date & Time": row.transactionDateAndTime,
        "Booking Date": row.bookingDateFormatted,
        "Billing Name": row.billingName,
        "Billing Email": row.billingEmail,
        "Billing Phone": row.billingPhone,
        "Company name": row.companyName,
        "GST number": row.gstNumber,
        "Billing Address": row.billingAddress,
        Country: row.country,
        State: row.state,
        City: row.city,
        "Zip Code": row.zip,
        "Payment Method": row.type,
        "Payment Status": row.status,

        "Coupon Code": row.couponCode || "-",
        Discount: row.discount || 0,
        Tax: row.taxAmount || 0,
        paidAmount: row.paid,
        // attendees: row.attendeesCount,
      };

      // 3️⃣ Add dynamic attendee columns
      for (let i = 0; i < maxAttendees; i++) {
        const att = row.attendeesData?.[i]; // <-- attendees array

        baseRow[`Attendee_${i + 1}`] = att
          ? `Name: ${att.name}\nemail: ${att.email}\nphone: ${att.phone}\nticket name: ${att.ticketName}\nTicket No: ${att.ticketNumber}`
          : "";
      }

      return baseRow;
    });

    // 4️⃣ Generate headers dynamically
    const headers = Object.keys(data[0]);

    const csvRows: string[] = [];
    csvRows.push(headers.join(","));

    data.forEach((row) => {
      const values = headers.map((h) => {
        const val = row[h] ?? "";

        // CSV safe escape
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    });

    // 5️⃣ Download CSV
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;

    const filename = event?.title ? `${event?.title}.csv` : "all-tickets.csv";
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="events-main">
      <Card className="event-card p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div
            className="d-flex align-items-center gap-2 cursor-pointer"
            onClick={() => navigate(-1)}
            style={{ minWidth: 0 }}>
            <span style={{ fontSize: 22, fontWeight: 700 }}>←</span>
            <div>
              <div className="page-title m-0">Event Booking Details</div>
              {/* <div style={{ fontSize: 13, color: "#666" }}>
                Manage booking records
              </div> */}
            </div>
          </div>
          
        </div>

        {/* Event summary card (same as before) */}
        {/* {event && (
          <div
            className="mb-4 p-3 rounded bg-white"
            style={{ border: "1px solid #eee" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 30,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{ fontSize: 13, color: "#777", marginBottom: 6 }}
                >
                  Event
                </div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {event?.title}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{ fontSize: 13, color: "#777", marginBottom: 6 }}
                >
                  Date
                </div>
                <div style={{ fontSize: 15 }}>
                  {HelperService.dateFormatter(event?.start_date)} –{" "}
                  {HelperService.dateFormatter(event?.end_date)}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{ fontSize: 13, color: "#777", marginBottom: 6 }}
                >
                  Location
                </div>
                <div style={{ fontSize: 15 }}>
                  {event?.location || "-"}
                </div>
              </div>
            </div>
          </div>
        )} */}
        {event && (
          <div className="mb-3 px-3 py-2 rounded bg-white" style={{ border: "1px solid #eee" }}>
            <div style={{ fontSize: 13, color: "#777" }}>Event</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{event?.title}</div>
          </div>
        )}

        {/* 🔥 Ticket Tabs */}
        {uniqueTickets.length > 1 && (
          <div style={{ marginBottom: 20 }}>
            <button
              onClick={() => {
                setSelectedTicket("");
                setCurrentPage(1);
              }}
              style={{
                marginRight: 10,
                padding: "6px 14px",
                borderRadius: 6,
                background: !selectedTicket ? "#007bff" : "#fff",
                color: !selectedTicket ? "#fff" : "#000",
                border: "1px solid #ddd",
                cursor: "pointer",
              }}>
              All
            </button>

            {uniqueTickets.map((t) => (
              <button
                key={t}
                onClick={() => {
                  setSelectedTicket(t);
                  setCurrentPage(1);
                }}
                style={{
                  marginRight: 10,
                  padding: "6px 14px",
                  borderRadius: 6,
                  background: selectedTicket === t ? "#007bff" : "#fff",
                  color: selectedTicket === t ? "#fff" : "#000",
                  border: "1px solid #ddd",
                  cursor: "pointer",
                }}>
                {t}
              </button>
            ))}

            {/* 🚀 EXPORT BUTTON */}
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
          <div className="mb-1" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#0c0c0c", fontWeight: 600 }}>Search</label>
            <input
              type="text"
              placeholder="Booking id, name, email, transaction"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: 36,
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #ccc",
                width: 240,
                fontSize: 13,
              }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#0c0c0c", fontWeight: 600 }}>Payment Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: 36,
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #ccc",
                width: 130,
                fontSize: 14,
                backgroundColor: "#fff",
                marginBottom: 14,
              }}>
              <option value="">All Status</option>
              <option value="PAID">Paid</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#0c0c0c", fontWeight: 600 }}>
              Payment Method
            </label>

            <select
              value={selectedPaymentMethod}
              onChange={(e) => {
                setSelectedPaymentMethod(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: 36,
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #ccc",
                width: 140,
                fontSize: 14,
                backgroundColor: "#fff",
                marginBottom: 14,
              }}
            >
              <option value="">All Methods</option>
              <option value="REQUEST">REQUEST</option>
              <option value="BOOKING">BOOKING</option>
            </select>
          </div>

          {/* 📅 From Date */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#0c0c0c", fontWeight: 600 }}>Booking Date</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  height: 36,
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  fontSize: 14,
                  marginBottom: 14,
                }}
              />


              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  height: 36,
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  fontSize: 14,
                  marginBottom: 14,
                }}
              />
            </div>
          </div>
          {pagePermission.canView && (
          <button
              onClick={handleExport}
              className="ms-auto"
              style={{
                height: 36,
                padding: "6px 18px",
                background: "#1640d6",
                color: "#fff",
                borderRadius: 6,
                border: "none",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: 14,
                whiteSpace: "nowrap",
              }}
            >
              Export
          </button>
          )}
 
        </div>

        {/* Bookings table */}
        <Card className="p-3" style={{ border: "1px solid #f0f0f0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}>
            <h5 style={{ margin: 0 }}>Bookings</h5>
            <div style={{ color: "#666", fontSize: 13 }}>
              {totalCount} booking{totalCount !== 1 ? "s" : ""}
            </div>
          </div>

          <CustomTable
            columns={[
              { id: "bookingId", label: "Booking Id", minWidth: 140 },
              { id: "type", label: "Payment Method", minWidth: 140 },
              // { id: "ticket", label: "Ticket", minWidth: 160 },
              { id: "transactionId", label: "Order ID", minWidth: 160 },
              {
                id: "billingName",
                label: "Billing Name",
                minWidth: 160,
              },
              {
                id: "billingEmail",
                label: "Billing Email",
                minWidth: 160,
              },
              // {
              //   id: "billingPhone",
              //   label: "Billing Phone",
              //   minWidth: 160,
              // },
              {
                id: "transactionDateAndTime",
                label: "Transaction Date & Time",
                minWidth: 160,
              },
              {
                id: "bookingDateFormatted",
                label: "Booking Date",
                minWidth: 160,
              },
              {
                id: "paidAmount",
                label: "Paid Amount",
                minWidth: 160,
              },
              {
                id: "attendeesCount",
                label: "Attendees",
                minWidth: 90,
                align: "center",
              },
              {
                id: "statusTag",
                label: " Payment Status",
                minWidth: 120,
                align: "center",
              },
              {
                id: "viewAction",
                label: "View",
                minWidth: 80,
                align: "center",
              },
            ]}
            rows={bookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
          />
        </Card>
      </Card>
    </div>
  );
};

export default EventBookings;